import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router'

// @material-ui
import {
  Typography,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import { withStyles } from '@material-ui/styles'
import {
  Help,
  ExpandMore,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
} from '@material-ui/icons'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { State } from '_core/components/State'
import { VerifyMobileStepPhone } from '_core/views/VerifyMobileStepPhone'
import { VerifyMobileStepCode } from '_core/views/VerifyMobileStepCode'
import { VerifyEmailStepForm } from '_core/views/VerifyEmailStepForm'
import { VerifyEmailStepCount } from '_core/views/VerifyEmailStepCount'
import { VerifyPassportStepForm } from '_core/views/VerifyPassportStepForm'
import { VerifyPassportStepInfo } from '_core/views/VerifyPassportStepInfo'

// style
import { profileVerificationStyle } from './profileVerificationStyle'

const RawProfileVerification = props => {
  const { classes, getLangString: l } = props

  const {
    request: { queue },
    config: {
      API_METHOD,
      ROUTE_URL_PARAM,
      VERIFICATION_TYPE,
      VERIFICATION_STATUS,
    },
  } = useGlobal()

  const history = useHistory()
  const location = useLocation()
  const [nodeKey, setNodeKey] = useState(0)

  const types = Object.values(VERIFICATION_TYPE)
  const search = new URLSearchParams(location.search)
  const activePanel = search.get(ROUTE_URL_PARAM.VERIFY)

  const setActivePanel = type => {
    if (type == null) {
      search.delete(ROUTE_URL_PARAM.VERIFY)
    } else {
      search.set(ROUTE_URL_PARAM.VERIFY, type)
    }

    if (type !== VERIFICATION_TYPE.EMAIL) {
      search.delete(ROUTE_URL_PARAM.CODE)
    }

    location.search = search.toString()
    history.push(location)
  }

  activePanel != null && !types.includes(activePanel) && setActivePanel()

  const update = () => setNodeKey(k => k + 1)
  const getVerification = () => queue(API_METHOD.VISITOR_VERIFICATION)

  const forms = {
    [VERIFICATION_TYPE.MOBILE]: {
      [VERIFICATION_STATUS.BLANK]: VerifyMobileStepPhone,
      [VERIFICATION_STATUS.PENDING]: VerifyMobileStepCode,
    },
    [VERIFICATION_TYPE.EMAIL]: {
      [VERIFICATION_STATUS.BLANK]: VerifyEmailStepForm,
      [VERIFICATION_STATUS.PENDING]: VerifyEmailStepCount,
    },
    [VERIFICATION_TYPE.PASSPORT]: {
      [VERIFICATION_STATUS.BLANK]: VerifyPassportStepForm,
      [VERIFICATION_STATUS.PENDING]: VerifyPassportStepInfo,
    },
  }

  const renderForm = (type, data) => {
    const status =
      data.state === VERIFICATION_STATUS.PENDING &&
      (data.create_timeout > 0 || data.create_timeout == null)
        ? VERIFICATION_STATUS.PENDING
        : VERIFICATION_STATUS.BLANK

    const Form = forms[type][status]
    const childProps = { update }

    if (
      data.state === VERIFICATION_STATUS.PENDING &&
      data.create_timeout != null
    ) {
      if (data.create_timeout > 0) {
        childProps.time = data.create_timeout
      } else {
        childProps.isExpired = true
      }
    }

    return <Form {...childProps} />
  }

  const renderPanel = (type, children) => (
    <Accordion
      key={type}
      square={true}
      elevation={0}
      expanded={activePanel === type}
      classes={{ root: classes.panel }}
      onChange={(event, expanded) =>
        setActivePanel(expanded ? type : undefined)
      }
    >
      {children}
    </Accordion>
  )

  const renderBlank = (type, children) => [
    <AccordionSummary
      expandIcon={<ExpandMore />}
      classes={{ root: classes.panelSummary }}
    >
      <ListItemIcon>
        <Help className={`${classes.panelIcon} ${classes.panelIconBlank}`} />
      </ListItemIcon>
      <ListItemText
        primary={l(`r._.profile.verify.${type}.titleblank`)}
        primaryTypographyProps={{
          variant: 'subtitle2',
          className: `${classes.panelTitle} ${classes.panelTitleBlank}`,
        }}
      />
    </AccordionSummary>,
    <AccordionDetails
      className={`${classes.panelDetails} ${classes.panelDetailsBlank}`}
    >
      {children}
    </AccordionDetails>,
  ]

  const renderFailure = (type, children, data) => [
    <AccordionSummary
      expandIcon={<ExpandMore />}
      classes={{ root: classes.panelSummary }}
    >
      <ListItemIcon>
        <ErrorOutline
          className={`${classes.panelIcon} ${classes.panelIconFailure}`}
        />
      </ListItemIcon>
      <ListItemText
        primary={l(`r._.profile.verify.${type}.titlefailure`)}
        primaryTypographyProps={{
          variant: 'subtitle2',
          className: `${classes.panelTitle} ${classes.panelTitleFailure}`,
        }}
      />
    </AccordionSummary>,

    <AccordionDetails
      className={`${classes.panelDetails} ${classes.panelDetailsFailure}`}
    >
      <Alert
        severity="error"
        icon={() => null}
        className={classes.errorMessage}
      >
        <AlertTitle>
          <Typography variant="h6">{l('r._.rejection.reason')}</Typography>
        </AlertTitle>
        
        {data.note?.split(/\n/g).map((line, index) => <div style={{ minHeight: '1.43em' }} key={index}>{line}</div>)}
      </Alert>

      {children}
    </AccordionDetails>,
  ]

  const renderPending = (type, children) => [
    <AccordionSummary
      expandIcon={<ExpandMore />}
      classes={{ root: classes.panelSummary }}
    >
      <ListItemIcon>
        <HourglassEmpty
          className={`${classes.panelIcon} ${classes.panelIconPending}`}
        />
      </ListItemIcon>
      <ListItemText
        primary={l(`r._.profile.verify.${type}.titlepending`)}
        primaryTypographyProps={{
          variant: 'subtitle2',
          className: `${classes.panelTitle} ${classes.panelTitlePending}`,
        }}
      />
    </AccordionSummary>,
    <AccordionDetails
      className={`${classes.panelDetails} ${classes.panelDetailsPending}`}
    >
      {children}
    </AccordionDetails>,
  ]

  const renderSuccess = type => (
    <div className={classes.panelSummarySuccess} key={type}>
      <ListItemIcon>
        <CheckCircle
          className={`${classes.panelIcon} ${classes.panelIconSuccess}`}
        />
      </ListItemIcon>
      <ListItemText
        primary={l(`r._.profile.verify.${type}.titlesuccess`)}
        primaryTypographyProps={{
          variant: 'caption',
          className: `${classes.panelTitle} ${classes.panelTitleSuccess}`,
        }}
      />
    </div>
  )

  return (
    <State
      key={nodeKey}
      getProps={{ verification: getVerification }}
      render={({ verification }) =>
        Object.keys(verification.types)
          .filter(type => types.includes(type))
          .map(type => {
            const data = verification.types[type]

            switch (data.state) {
              case VERIFICATION_STATUS.BLANK:
                return renderPanel(
                  type,
                  renderBlank(type, renderForm(type, data))
                )

              case VERIFICATION_STATUS.PENDING:
                return renderPanel(
                  type,
                  renderPending(type, renderForm(type, data))
                )

              case VERIFICATION_STATUS.SUCCESS:
                return renderSuccess(type)

              case VERIFICATION_STATUS.FAILURE:
                return renderPanel(
                  type,
                  renderFailure(type, renderForm(type, data), data)
                )

              default:
                return null
            }
          })
      }
    />
  )
}

RawProfileVerification.defaultProps = {}

RawProfileVerification.propTypes = {
  // `withStyles` HOC props
  classes: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const ProfileVerification = withLang(
  withStyles(profileVerificationStyle)(RawProfileVerification)
)
